import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star, MapPin, ShieldCheck } from "lucide-react";

const PRICE_LABELS = { 1: "$", 2: "$$", 3: "$$$", 4: "$$$$" };

function daysSince(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  } catch { return null; }
}

export default function VenueCard({ venue, compact = false }) {
  const { t } = useTranslation();
  const verifiedDays = daysSince(venue.last_verified_at);
  const verifiedLabel =
    verifiedDays == null ? null
    : verifiedDays === 0 ? t("map.verifiedToday")
    : verifiedDays === 1 ? t("map.verifiedYesterday")
    : t("map.verifiedDaysAgo", { count: verifiedDays });
  return (
    <Link
      to={`/local/${venue.id}`}
      data-testid={`venue-card-${venue.slug}`}
      className="group block bg-[#1A1A1A] border-2 border-[#1A1A1A] hover:border-[#FEBA01] hover-lift transition-colors overflow-hidden"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[#0A0A0A]">
        {venue.cover_photo ? (
          <img
            src={venue.cover_photo}
            alt={venue.name}
            className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
          />
        ) : (
          <div className="w-full h-full bg-grain" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          {venue.featured && (
            <span className="bg-[#FEBA01] text-black font-display text-xs uppercase px-2 py-1">
              Top Kiltro
            </span>
          )}
          <span className="bg-black/80 text-[#FEBA01] font-mono text-[10px] uppercase px-2 py-1 border border-[#FEBA01]/40">
            {PRICE_LABELS[venue.price_level] || "$$"}
          </span>
        </div>
        <div className="absolute bottom-3 right-3 flex flex-col items-end gap-1">
          <div className="flex items-center gap-1 bg-black/80 px-2 py-1 border border-[#FEBA01]/40">
            <Star size={12} className="fill-[#FEBA01] text-[#FEBA01]" />
            <span className="font-mono text-xs text-[#FEBA01]">{venue.rating_avg.toFixed(1)}</span>
          </div>
          <span className="font-mono text-[9px] uppercase tracking-widest text-[#A3A3A3] bg-black/60 px-1.5 py-0.5">
            Fuente · {venue.rating_source || "Google"}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display text-2xl uppercase leading-none tracking-tight group-hover:text-[#FEBA01] transition-colors">
          {venue.name}
        </h3>
        <div className="flex items-center gap-1 mt-2 text-[#A3A3A3] text-xs font-mono uppercase tracking-wider">
          <MapPin size={12} />
          {venue.neighborhood}
        </div>
        {verifiedLabel && (
          <div
            className="mt-2 inline-flex items-center gap-1 text-[#FEBA01] text-[10px] font-mono uppercase tracking-widest border border-[#FEBA01]/30 px-2 py-0.5"
            data-testid={`verified-badge-${venue.slug}`}
            title={`Última verificación: ${venue.last_verified_at?.slice(0, 10)}${venue.verified_source ? ` · Fuente: ${venue.verified_source}` : ""}`}
          >
            <ShieldCheck size={10} /> {verifiedLabel}
          </div>
        )}
        {!compact && (
          <p className="mt-3 text-sm text-[#A3A3A3] line-clamp-2">{venue.description}</p>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(venue.beer_types || []).slice(0, 3).map((b) => (
            <span key={b} className="tag">{b}</span>
          ))}
          {(venue.ambient || []).slice(0, 1).map((a) => (
            <span key={a} className="tag-muted">{a}</span>
          ))}
        </div>
      </div>
    </Link>
  );
}
